import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import StudioPageShell from '../../components/studio/StudioPageShell';
import {
  listV2Apps, createV2App, getV2App, studioV2NewAppDraft, deleteV2App,
} from '../../api/studioV2.api';
import { getColorsFromConfig } from '../../components/Dashboard/AppThemeProvider';
import NewAppWizard from '../../components/studio/appBuilder/NewAppWizard';
import { LayoutSketch } from '../../components/studio/appBuilder/panelVisuals';
import { layoutThumbnail } from '../../components/studio/appBuilder/appBuilderModel';

const STATUS_CHIP = {
  published: { label: 'Live', color: 'success' },
  draft: { label: 'Draft', color: 'warning' },
  archived: { label: 'Archived', color: 'default' },
};

function relativeTime(date) {
  if (!date) return '';
  const diff = Date.now() - new Date(date).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(date).toLocaleDateString();
}

function AppCard({
  row, busy, onDuplicate, onDelete,
}) {
  const def = row.definition || {};
  const colors = getColorsFromConfig(def.theme || {});
  const status = STATUS_CHIP[row.status] || STATUS_CHIP.draft;
  const name = row.name || def.name || 'Untitled app';

  return (
    <Paper
      elevation={0}
      data-testid={`app-card-${row._id}`}
      sx={{
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow .15s, transform .15s',
        '&:hover': { boxShadow: '0 10px 28px rgba(15,23,42,.10)', transform: 'translateY(-2px)' },
      }}
    >
      <Box
        component={RouterLink}
        to={`/studio/apps/${row._id}`}
        aria-label={`Edit ${name}`}
        sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <Box sx={{
          p: 2, pb: 1.5, background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary || colors.primary})`,
        }}
        >
          <Box sx={{ bgcolor: 'rgba(255,255,255,.92)', borderRadius: 1.5, p: 0.75 }}>
            <LayoutSketch columns={layoutThumbnail(def)} accent={colors.primary} height={56} />
          </Box>
        </Box>
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{name}</Typography>
            <Chip
              size="small"
              label={status.label}
              color={status.color}
              variant={row.status === 'published' ? 'filled' : 'outlined'}
              sx={{ height: 22, fontWeight: 600 }}
            />
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              minHeight: 40, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}
          >
            {def.description || 'No description yet.'}
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" alignItems="center" sx={{ px: 2, pb: 1.25, mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {row.updatedAt ? `Edited ${relativeTime(row.updatedAt)}` : ''}
          {row.accessRole && row.accessRole !== 'owner' ? ` · Shared with you (${row.accessRole})` : ''}
        </Typography>
        {row.status === 'published' && (
          <Tooltip title="Open the live app">
            <IconButton size="small" component="a" href={`/v2/p/${row._id}`} target="_blank" rel="noopener noreferrer" aria-label="Open the live app">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Duplicate">
          <span>
            <IconButton size="small" aria-label="Duplicate app" disabled={busy} onClick={() => onDuplicate(row._id)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {row.accessRole === 'owner' && (
          <Tooltip title="Delete">
            <span>
              <IconButton size="small" aria-label="Delete app" disabled={busy} onClick={() => onDelete(row._id)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {busy && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Stack>
    </Paper>
  );
}

function StudioAppsListPage() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const importRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { apps: rows, error: err } = await listV2Apps();
    if (err) setError(err);
    setApps(rows || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setError(null);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const raw = JSON.parse(String(reader.result || '{}'));
        if (raw.schemaVersion !== 'app/v2') {
          setError('That file is not an exported Studio app.');
          return;
        }
        const shell = studioV2NewAppDraft(raw.name || 'Imported app');
        const body = {
          ...raw,
          id: shell.id,
          name: raw.name && String(raw.name).trim() ? String(raw.name).trim() : shell.name,
          schemaVersion: 'app/v2',
        };
        const { app, error: err } = await createV2App(body);
        if (err) setError(err);
        else if (app?._id) navigate(`/studio/apps/${app._id}`);
        else await load();
      } catch (err) {
        setError(err?.message || 'That file could not be read.');
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const onDuplicate = async (mongoId) => {
    setBusyId(mongoId);
    setError(null);
    const { app, error: gerr } = await getV2App(mongoId);
    if (gerr || !app?.definition) {
      setError(gerr || 'Could not load app');
      setBusyId(null);
      return;
    }
    const shell = studioV2NewAppDraft('x');
    const d = app.definition;
    const baseName = d.name != null && String(d.name).trim() ? String(d.name).trim() : 'Untitled app';
    const { app: created, error: cerr } = await createV2App({
      ...d,
      id: shell.id,
      name: `Copy of ${baseName}`,
      schemaVersion: d.schemaVersion || 'app/v2',
    });
    setBusyId(null);
    if (cerr) {
      setError(cerr);
      return;
    }
    if (created?._id) navigate(`/studio/apps/${created._id}`);
  };

  const onDelete = async (mongoId) => {
    if (!window.confirm('Delete this app? This cannot be undone.')) return;
    setBusyId(mongoId);
    setError(null);
    const { error: derr } = await deleteV2App(mongoId);
    setBusyId(null);
    if (derr) {
      setError(derr);
      return;
    }
    await load();
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((row) => {
      if (statusFilter !== 'all' && (row.status || 'draft') !== statusFilter) return false;
      if (!q) return true;
      const hay = `${row.name || ''} ${row.definition?.name || ''} ${row.definition?.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [apps, query, statusFilter]);

  return (
    <StudioPageShell
      title="Apps"
      subtitle="Put your AI flows in front of people: a chat, plus panels that show what the flow finds."
      backTo="/studio"
      backLabel="Studio"
      maxWidth={1280}
      actions={(
        <>
          <input ref={importRef} type="file" accept="application/json,.json" hidden onChange={onImportFile} />
          <Button
            variant="outlined"
            startIcon={importing ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => importRef.current?.click()}
            disabled={importing}
            sx={{ textTransform: 'none' }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setWizardOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            data-testid="new-app"
          >
            New app
          </Button>
        </>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={36} />
        </Box>
      ) : apps.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6, borderRadius: 3, border: 1, borderColor: 'divider', textAlign: 'center',
          }}
        >
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.secondary',
          }}
          >
            <ViewModuleIcon fontSize="large" />
          </Box>
          <Typography variant="h6" fontWeight={700}>No apps yet</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: 420, mx: 'auto', mt: 0.5, mb: 2.5,
            }}
          >
            Start from a ready-made template or a blank page. It takes about a minute.
          </Typography>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Create your first app
          </Button>
        </Paper>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              placeholder="Search apps"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ width: { sm: 320 }, bgcolor: 'background.paper' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, v) => v && setStatusFilter(v)}>
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.75 }}>All</ToggleButton>
              <ToggleButton value="published" sx={{ textTransform: 'none', px: 1.75 }}>Live</ToggleButton>
              <ToggleButton value="draft" sx={{ textTransform: 'none', px: 1.75 }}>Drafts</ToggleButton>
              <ToggleButton value="archived" sx={{ textTransform: 'none', px: 1.75 }}>Archived</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 2.5 }}>
            {filtered.map((row) => (
              <AppCard key={row._id} row={row} busy={busyId === row._id} onDuplicate={onDuplicate} onDelete={onDelete} />
            ))}
          </Box>
          {!filtered.length && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              No apps match.
            </Typography>
          )}
        </>
      )}
      <NewAppWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(app) => navigate(`/studio/apps/${app._id}`)}
      />
    </StudioPageShell>
  );
}

export default StudioAppsListPage;
